import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { getRoleHeader } from "@components/table/members/members-table-utils";
import {
  getTransactionAmount,
  getTransactionDescription,
  isConsumedTransaction,
  isExpiredTransaction,
} from "@components/table/transactions/transactions-table-utils";
import { ProjectId } from "@custom-types/base-types";
import {
  CreditSubscription,
  CreditTransaction,
} from "@custom-types/credit-types";
import { GroupTypes } from "@custom-types/group-types";
import { BaseMembersProps, SdbMemberTypes } from "@custom-types/member-types";
import { Markup } from "@custom-types/project-markups-types";
import { BaseProjectsProps, SdbProject } from "@custom-types/project-types";
import { SdbTeam } from "@custom-types/teams-types";
import { isGroupDetails } from "@custom-types/type-guards";
import { FormatDate } from "@hooks/use-date-time";
import { getMarkupAssigneeName } from "@pages/project-details/project-markups/markup-assignee-utils";
import { getMarkupStatusLabel } from "@pages/project-details/project-markups/status/markup-status-utils";
import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  getSubscriptionPackageName,
  getSubscriptionStatus,
} from "@utils/credit-transaction-utils";
import { formatUserRoleType } from "@utils/data-display";
import { exportDataByFormat } from "@utils/file-data-export-utils";
import { getProjectNumbersInGroup } from "@utils/group-utils";
import { getMemberNameById, getMembersMapById } from "@utils/member-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isTeam } from "@utils/team-utils";
import { DASH } from "@utils/ui-utils";
import {
  getMemberStatus,
  getPrettyName,
  isMemberActive,
} from "@utils/user-utils";
import { getCoordinateZUp } from "@utils/vector-utils";
import { DateTime } from "luxon";
import {
  getProjectArea,
  getProjectClientName,
  getProjectManager,
  getSphereViewerUrl,
} from "src/utils/project-utils";

/** The valid formats to export a file */
export type ExportFormat = "CSV" | "XLSX" | "JSON";

/**
 * Create file out of groups array and create a download handler
 */
export async function downloadGroupData(
  groups: GroupTypes[],
  formatDate: FormatDate,
  format: ExportFormat
): Promise<number> {
  const data = groups.map((group) => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: group.id,
    Name: group.name,
    "Created At": formatDate(group.createdAt),
    Managers: isGroupDetails(group) ? DASH : group.numManagers,
    Projects: getProjectNumbersInGroup(group) || DASH,
    "Sqft Assigned":
      group.sqftAssigned !== -1 ? group.sqftAssigned : "unrestricted",
    "Sqft Used": group.sqftUsed,
    /* eslint-enable */
  }));

  await exportDataByFormat({
    data,
    fileName: "Groups List",
    format,
  });
  return data.length;
}

/**
 * Create file out of teams array and create a download handler
 */
export async function downloadTeamData(
  teams: SdbTeam[],
  formatDate: FormatDate,
  format: ExportFormat
): Promise<number> {
  const data = teams.map((team) => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: team.id,
    Name: team.name,
    Description: team.description,
    "Number of Members": team.memberCount,
    "Created At": formatDate(team.createdAt),
    Role: formatUserRoleType(team.role),
    /* eslint-enable */
  }));

  await exportDataByFormat({
    data,
    fileName: `${capitalizeFirstLetter(TEAMS_DISPLAY_NAME)} List`,
    format,
  });
  return data.length;
}

interface DownloadMembersDataProps extends BaseMembersProps {
  /** The name of the file to be downloaded */
  fileName: string;

  /** The subject type of the member */
  subjectType: MemberTableSubject;

  /** The format to export the file */
  format: ExportFormat;
}

/**
 * Create file out of project members array and create a download handler
 */
export async function downloadMembersData({
  fileName,
  members,
  subjectType,
  format,
}: DownloadMembersDataProps): Promise<number> {
  const data = members.map((member) => {
    const userData = {
      ID: isTeam(member) ? member.email : member.identity,
      Name: isMemberActive(member) ? getPrettyName(member) : DASH,
      Email: isTeam(member) ? DASH : member.email,
      status: isTeam(member) ? DASH : getMemberStatus({ member }),
    };

    // Skipping Role column when subject type is team
    if (subjectType !== "team") {
      Object.assign(userData, {
        [getRoleHeader(subjectType)]: formatUserRoleType(member.role),
      });
    }

    return userData;
  });

  await exportDataByFormat({
    data,
    fileName,
    format,
  });
  return data.length;
}

interface DownloadProjectsDataProps extends BaseProjectsProps {
  /** The name of the file to be downloaded */
  fileName: string;

  /** The function to format date. Needs to be passed manually when not used in a hook */
  formatDate: FormatDate;

  /** The format to export the file */
  format: ExportFormat;
}

/**
 * Create file out of project array and create a download handler
 */
export async function downloadProjectsData({
  fileName,
  projects,
  formatDate,
  format,
}: DownloadProjectsDataProps): Promise<number> {
  const data = projects.map((project) => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    "Project Name": project.name,
    Client: getProjectClientName(project),
    Area: getProjectArea(project),
    "Project Manager Name": projectManagerName(project),
    "Project Manager Email": getProjectManager(project)?.email || DASH,
    Edited: formatDate(project.modifiedAt),
    "Created At": formatDate(project.createdAt),
    "Project Link": getSphereViewerUrl({ projectId: project.id }).href,
    /* eslint-enable */
  }));

  await exportDataByFormat({
    data,
    fileName,
    format,
  });
  return data.length;
}

/**
 * Returns the project manager name
 * If the project manager is active, it will return the name otherwise it will return DASH
 */
function projectManagerName(project: SdbProject): string {
  const projectManager = getProjectManager(project);

  if (projectManager && isMemberActive(projectManager)) {
    return getPrettyName(projectManager);
  }
  return DASH;
}

export interface BaseDownloadMarkupsDataProps {
  /** List of selected markups in table */
  markups: Markup[];

  /** List of company members */
  companyMembers: SdbMemberTypes[];

  /** List of project members */
  projectMembers: FaroCoreApiTypes.ProjectMemberBase[];

  /** The name of the file to be downloaded */
  fileName: string;

  /** Callback to return the URL of the markup either in WebEditor or Viewer */
  markupHref: (markup: Markup) => string | undefined;

  /** The function to format date. Needs to be passed manually when not used in a hook */
  formatDate: FormatDate;
}

export interface DownloadMarkupsDataProps extends BaseDownloadMarkupsDataProps {
  /** The format to export the file */
  format: ExportFormat;
}

/**
 * Interface for the global pose of a markup with Z as the up-axis to be
 * in-line with the Sphere Viewer.
 * It contains the X, Y, and Z coordinates of the global pose without scale or rotation.
 * If the global pose is not available, the values will be undefined.
 */
interface MarkupGlobalPose {
  GlobalPoseX?: number;
  GlobalPoseY?: number;
  GlobalPoseZ?: number;
}

function getMarkupGlobalPose(markup: Markup): MarkupGlobalPose {
  if (!markup.globalPose) {
    return {
      GlobalPoseX: undefined,
      GlobalPoseY: undefined,
      GlobalPoseZ: undefined,
    };
  }
  const { x, y, z } = getCoordinateZUp(markup.globalPose.pos);
  return {
    GlobalPoseX: x,
    GlobalPoseY: y,
    GlobalPoseZ: z,
  };
}

/**
 * Create file out of markups array and create a download handler
 */
export async function downloadMarkupData({
  markups,
  companyMembers,
  projectMembers,
  fileName,
  markupHref,
  formatDate,
  format,
}: DownloadMarkupsDataProps): Promise<number> {
  const data = markups.map((markup) => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    ID: markup.id,
    Name: markup.name,
    Assignee: getMarkupAssigneeName({
      assignee: markup.assignee,
      companyMembers,
      projectMembers,
    }),
    "Created At": formatDate(markup.createdAt),
    "Created By": getMemberNameById({
      memberId: markup.createdBy,
      companyMembers,
      projectMembers,
    }),
    "Due Date": markup.dueDate?.value && formatDate(markup.dueDate.value),
    Status: getMarkupStatusLabel(markup.status?.value),
    Tags: markup.labels?.map((label) => label.name).join(", "),
    "Number of Attachments": markup.attachmentsCount,
    Description: markup.descr,
    Url: markupHref(markup),
    ...getMarkupGlobalPose(markup),
    /* eslint-enable */
  }));

  if (data.length === 0) {
    return 0;
  }

  await exportDataByFormat({
    data,
    fileName,
    format,
  });
  return data.length;
}

interface DownloadTransactionsDataProps extends BaseProjectsProps {
  /** List of credit transactions */
  transactions: CreditTransaction[];

  /** List of company members */
  companyMembers: SdbMemberTypes[];

  /** The name of the file to be downloaded */
  fileName: string;

  /** The function to format date. Needs to be passed manually when not used in a hook */
  formatDate: FormatDate;

  /** The format to export the file */
  format: ExportFormat;
}

interface DownloadPurchasesDataProps {
  /** List of credit subscriptions */
  subscriptions: CreditSubscription[];

  /** The name of the file to be downloaded */
  fileName: string;

  /** The function to format date. Needs to be passed manually if not used in a hook */
  formatDate: FormatDate;

  /** The format to export the file */
  format: ExportFormat;
}

/** Create file out of transactions array and create a download handler */
export async function downloadTransactionsData({
  transactions,
  companyMembers,
  projects,
  fileName,
  formatDate,
  format,
}: DownloadTransactionsDataProps): Promise<number> {
  // Cache the project names using the project ID as the key
  const projectMap = new Map<ProjectId | undefined, string>(
    projects.map((project) => [project.id, project.name])
  );
  const membersMap = getMembersMapById(companyMembers);

  const data = transactions.map((transaction) => {
    const member = transaction.context.userId
      ? membersMap.get(transaction.context.userId)
      : undefined;

    const tokenAmount = getTransactionAmount(transaction);
    const amountToShow =
      isConsumedTransaction(transaction) || isExpiredTransaction(transaction)
        ? -Math.abs(tokenAmount)
        : Math.abs(tokenAmount);

    return {
      /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
      Date: formatDate(transaction.timestamp, DateTime.DATE_MED),
      "User Name": member?.name ?? DASH,
      "User Email": member?.email ?? DASH,
      Description: getTransactionDescription(transaction),
      "Project ID": transaction.context.projectId ?? DASH,
      "Project Name": transaction.context.projectId
        ? projectMap.get(transaction.context.projectId)
        : DASH,
      [capitalizeFirstLetter(CREDITS_DISPLAY_NAME)]: amountToShow,
      /* eslint-enable */
    };
  });

  await exportDataByFormat({
    data,
    fileName,
    format,
  });
  return data.length;
}

/** Create file out of credit subscriptions array and create a download handler */
export async function downloadPurchasesData({
  subscriptions,
  fileName,
  formatDate,
  format,
}: DownloadPurchasesDataProps): Promise<number> {
  const creditsRemainingHeader = `${capitalizeFirstLetter(
    CREDITS_DISPLAY_NAME
  )} Remaining`;
  const data = subscriptions.map((subscription) => ({
    /* eslint-disable @typescript-eslint/naming-convention -- Many of the keys are used for file export */
    Package: getSubscriptionPackageName(subscription),
    "Purchased Date": formatDate(subscription.startDate),
    "Expiration Date": formatDate(subscription.expiresAt),
    Status: getSubscriptionStatus(subscription),
    [creditsRemainingHeader]: subscription.availableCredit,
    /* eslint-enable */
  }));

  await exportDataByFormat({
    data,
    fileName,
    format,
  });
  return data.length;
}
